
import Vue from 'vue';
import { get$bindFirestoreOptions, TeamUserMixin } from '@/mixins';
import { db } from '@/firebase';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { ROUTER } from '@/router/constants';
import { mapActions, mapGetters, mapState } from 'vuex';
import { Team, TeamMember } from '@run-diffusion/shared';
import { MIRROR_LAG_REFETCH_MS } from '@/utils/cursorWalk';
import { TEAMS_CHANGED } from '@/utils/teamsMirrorLag';
import { TEAMS_API_BASE_URL, TeamsSummaryResult, fetchTeamsSummary, teamsSummaryFailure } from '@/utils/teamsApi';
import { InternalApiCredentials } from '@/utils/internalApi';
import { forgetWebClientDeviceIfRejected, internalApiCredentials } from '@/services/internalApiCredentials';
import TeamMemberOverview from '@/views/Team/TeamMemberDashboard/TeamMemberOverview.vue';
import TeamMemberTeamsManagement from '@/views/Team/TeamMemberDashboard/TeamMemberTeamsManagement.vue';

export default Vue.extend({
	name: 'TeamMemberDashboard',
	metaInfo () {
		return {
			title: `Dashboard | ${this.currTeamMember?.email || 'Member'} | RunDiffusion`,
		};
	},
	mixins: [
		TeamUserMixin,
	],
	data () {
		return {
			loadingCurrTeamMember: false,
			currTeamMember: null,

			loadingCurrTeam: false,
			currTeam: null,

			// The card's count of the member's joined, active Teams (from the internal teams summary), the last
			// good one kept while a refetch runs; null until there is one
			joinedTeamsCount: null as number | null,
			loadingJoinedTeamsCount: false,
			joinedTeamsCountRequestVersion: 0,
			joinedTeamsCountRefetchTimer: null as number | null,
			stopListeningToTeamsChanges: null as (() => void) | null,
			isDestroyed: false,

			loadingTeamUserRoleDefs: false,
			teamUserRoleDefs: [],

			companyUserRoleDefs: [],
		};
	},
	computed: {
		...mapState([
			'user',
		]),
		...mapGetters([
			'displayLabels',
		]),
		companyId (): string | null {
			return this.currTeam?.companyId || null;
		},
		// Only a viewer who sees the header's Company Role badge reads the definitions that name it
		companyRoleDefsCompanyId (): string | null {
			return this.hasManageCompanyPermission ? this.companyId : null;
		},
		// Whose Teams the card counts, and in which company: either changing is another count
		joinedTeamsCountKey (): string {
			return JSON.stringify([this.companyId, this.currTeamMember?.email || null]);
		},
	},
	watch: {
		companyRoleDefsCompanyId: {
			immediate: true,
			handler (newVal: string | null, oldVal: string | null) {
				if (newVal !== oldVal) {
					this.bindCompanyUserRoleDefs(newVal);
				}
			},
		},
		'$route.params.teamId': {
			immediate: true,
			handler (newVal: string, oldVal: string) {
				if (newVal !== oldVal) {
					this.bindCurrTeam(newVal);
				}
			},
		},
		'$route.params.teamMemberId': {
			immediate: true,
			handler (newVal: string, oldVal: string) {
				if (newVal !== oldVal) {
					this.bindCurrTeamMember(newVal);
				}
			},
		},
		currTeamMember: {
			immediate: false,
			async handler (newVal: TeamMember, oldVal: TeamMember) {
				const newMemberId: string = newVal?.id;
				const oldMemberId: string = oldVal?.id;

				// Team member is removed, navigate away
				if (!newMemberId && oldMemberId) {
					this.routerReplace(this.$route, this.$router, {
						name: ROUTER.TEAM_DASHBOARD,
						params: {
							teamId: this.$route.params.teamId,
						},
						query: this.$route.query,
					});
					return;
				}
				if (newVal && this.currTeam) {
					await this.bindTeamUserRoleDefs(this.currTeam.companyId);
				}
			},
		},
		currTeam: {
			immediate: true,
			async handler (newVal: Team, oldVal: Team) {
				const newTeamId: string = newVal?.id || null;
				const oldTeamId: string = oldVal?.id || null;
				if (newTeamId !== oldTeamId && newVal && this.currTeamMember) {
					await this.bindTeamUserRoleDefs(newVal.companyId);
				}
			},
		},
		joinedTeamsCountKey: {
			immediate: true,
			handler (newVal: string, oldVal: string) {
				if (newVal === oldVal) return;
				// Another member's count is not this one's, not even for a moment
				this.joinedTeamsCount = null;
				this.loadJoinedTeamsCount();
			},
		},
	},
	created () {
		// A Team or membership changed anywhere (this table, a dialog, the account menu): count again once
		// the mirror has it
		this.stopListeningToTeamsChanges = TEAMS_CHANGED.onChanged(() => this.refetchJoinedTeamsCountAfterMirrorLag());
		// And a change made just before arriving is not in the mirror yet either
		const remainingMs: number = TEAMS_CHANGED.remainingMs();
		if (remainingMs > 0) this.refetchJoinedTeamsCountAfterMirrorLag(remainingMs);
	},
	beforeDestroy () {
		this.isDestroyed = true;
		this.clearJoinedTeamsCountRefetch();
		if (this.stopListeningToTeamsChanges) this.stopListeningToTeamsChanges();
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		async bindTeamUserRoleDefs (companyId: string) {
			try {
				this.loadingTeamUserRoleDefs = true;
				if (!companyId) return;
				// index created: teamUserRoleDefs: companyId Ascending isDeleted Ascending level Descending __name__ Descending
				const defsRef = db
					.collection('teamUserRoleDefs')
					.where('companyId', '==', companyId)
					.where('isDeleted', '==', false)
					.orderBy('level', 'desc');
				await this.$bind('teamUserRoleDefs', defsRef, get$bindFirestoreOptions());
			} catch (e) {
				console.error('Error binding team user role defs:', e);
			} finally {
				this.loadingTeamUserRoleDefs = false;
			}
		},
		// The header's Company Role badge names the role from these.
		async bindCompanyUserRoleDefs (companyId: string | null) {
			if (!companyId) {
				if (this.$firestoreRefs?.companyUserRoleDefs) this.$unbind('companyUserRoleDefs');
				this.companyUserRoleDefs = [];
				return;
			}
			try {
				// index created: companyUserRoleDefs: companyId Ascending isDeleted Ascending createdAt Descending __name__ Descending
				const defsRef = db
					.collection('companyUserRoleDefs')
					.where('companyId', '==', companyId)
					.where('isDeleted', '==', false)
					.orderBy('createdAt', 'desc');
				await this.$bind('companyUserRoleDefs', defsRef, get$bindFirestoreOptions());
			} catch (e) {
				console.error('Error binding company user role defs:', e);
			}
		},
		async bindCurrTeam (teamId: string) {
			// Handle routing away in bindCurrTeamMember method
			if (!teamId) return;

			try {
				this.loadingCurrTeam = true;
				await this.$bind(
					'currTeam',
					db
						.collection('teams')
						.doc(teamId),
					get$bindFirestoreOptions(),
				);
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error loading ${this.displayLabels.team}`,
					show: true,
				});
			} finally {
				this.loadingCurrTeam = false;
			}
		},
		async bindCurrTeamMember (teamMemberId: string) {
			const routeAway = () => {
				if (this.$route.params.teamId) {
					this.routerReplace(this.$route, this.$router, {
						name: ROUTER.TEAM_DASHBOARD,
						params: {
							teamId: this.$route.params.teamId,
						},
						query: this.$route.query,
					});
				} else {
					this.routerReplace(this.$route, this.$router, {
						name: ROUTER.COMPANY_DASHBOARD,
						query: this.$route.query,
					});
				}
			};
			if (!teamMemberId) routeAway();

			try {
				this.loadingCurrTeamMember = true;
				await this.$bind(
					'currTeamMember',
					db
						.collection('teams')
						.doc(this.$route.params.teamId)
						.collection('teamMembers')
						.doc(teamMemberId),
					get$bindFirestoreOptions(),
				);
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error loading member',
					show: true,
				});
				routeAway();
			} finally {
				this.loadingCurrTeamMember = false;
			}
		},
		/**
		 * How many active Teams the member has joined, counted in Postgres within
		 * what the viewer may see (every Team for MANAGE_TEAMS, else the ones they
		 * share), the same reading the table below lists. A failure keeps the last
		 * good number, and with none the card shows a dash rather than a 0.
		 */
		async loadJoinedTeamsCount (): Promise<void> {
			const companyId: string | null = this.companyId;
			const memberEmail: string | null = this.currTeamMember?.email || null;
			const version: number = ++this.joinedTeamsCountRequestVersion;
			if (!companyId || !memberEmail) {
				this.loadingJoinedTeamsCount = false;
				return;
			}
			this.loadingJoinedTeamsCount = true;
			let result: TeamsSummaryResult;
			try {
				const credentials: InternalApiCredentials | null = await internalApiCredentials();
				result = credentials
					? await fetchTeamsSummary({
						baseUrl: TEAMS_API_BASE_URL,
						...credentials,
						request: {
							companyId,
							accountTeamId: this.$store.state.team?.id || null,
							memberEmail,
							memberStatus: 'joined',
						},
					})
					: { ok: false, reason: 'unauthorized' };
			} catch (e) {
				console.error('Error counting the member\'s teams:', e);
				result = { ok: false, reason: 'failed' };
			}
			if (this.isDestroyed || version !== this.joinedTeamsCountRequestVersion) return;
			this.loadingJoinedTeamsCount = false;
			if (result.ok === true) {
				this.joinedTeamsCount = result.summary.active.teamCount;
			} else {
				console.error('Error counting the member\'s teams:', result);
				// A device the server no longer takes is forgotten, so the next ask registers a new one
				forgetWebClientDeviceIfRejected(teamsSummaryFailure(result)?.code);
			}
		},
		// After a change noted on the teams channel: once the mirror has it. At most one pending.
		refetchJoinedTeamsCountAfterMirrorLag (delayMs: number = MIRROR_LAG_REFETCH_MS) {
			if (this.isDestroyed) return;
			this.clearJoinedTeamsCountRefetch();
			this.joinedTeamsCountRefetchTimer = window.setTimeout(() => {
				this.joinedTeamsCountRefetchTimer = null;
				if (this.isDestroyed) return;
				this.loadJoinedTeamsCount();
			}, delayMs);
		},
		clearJoinedTeamsCountRefetch () {
			if (this.joinedTeamsCountRefetchTimer === null) return;
			window.clearTimeout(this.joinedTeamsCountRefetchTimer);
			this.joinedTeamsCountRefetchTimer = null;
		},
	},
	components: {
		TeamMemberOverview,
		TeamMemberTeamsManagement,
	},
});
